import { useContext, useEffect, useRef } from 'react';
import { SessionContext } from '@contexts/SessionProvider';
import { UserContext } from '@contexts/UserProvider';
import { getMessagesBySessionId } from '@config/getSessionApis';
import { preprocessSessionMessages } from '@utils/index';

/**
 * Hook that watches streamMessagesConsumer for the "Code loading finished" signal.
 * When detected, it fetches the final messages from the API, updates history/thread,
 * marks generation as complete, and clears the stream buffers.
 *
 * This logic was previously embedded inside StreamingCodePanel's render cycle.
 * Extracting it into a hook lets us replace the visible streaming panel with
 * any overlay UI while keeping the completion detection working.
 */
const useStreamCompletion = () => {
    const {
        streamMessagesConsumer,
        sessionId,
        generationSessionId,
        setIsGenerateComplete,
        setStreamMessagesConsumer,
        setStreamMessageProvider,
        setShowCodeStream,
        setThread,
        setHistory,
        setCurrentChatSessionId,
        setGenerationStatus,
    } = useContext(SessionContext);
    const { userEmail } = useContext(UserContext);

    const completionTriggeredRef = useRef(false);

    useEffect(() => {
        if (streamMessagesConsumer.length === 0) {
            completionTriggeredRef.current = false;
        }
    }, [streamMessagesConsumer.length]);

    useEffect(() => {
        if (completionTriggeredRef.current) return;

        if (generationSessionId && generationSessionId !== sessionId) return;

        const hasFinished = streamMessagesConsumer.some((message) => {
            if (!message) return false;
            const content = message.split(':').slice(1).join(':').trim();
            return content === 'Code loading finished';
        });

        if (!hasFinished) return;

        completionTriggeredRef.current = true;

        getMessagesBySessionId(userEmail, sessionId).then((response) => {
            const historialMessagesResult = response.resultObject;
            if (historialMessagesResult && historialMessagesResult.length) {
                const activeSession = preprocessSessionMessages(historialMessagesResult);
                if (activeSession && activeSession[0] && activeSession[0][0]) {
                    setCurrentChatSessionId(activeSession[0][0].chatSessionId);
                }
                setIsGenerateComplete(true);
                setShowCodeStream(false);
                setGenerationStatus(null);
                setHistory(activeSession);
                setThread(activeSession);
                setStreamMessagesConsumer([]);
                setStreamMessageProvider([]);
            }
        });
    }, [streamMessagesConsumer]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useStreamCompletion;
